import { useTranslation } from "react-i18next";
import i18n from "@/lib/i18n";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useLocation } from "wouter";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useApiRequest } from "@/hooks/use-api";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { useOrganization, useUser, SignUp, useClerk } from "@clerk/clerk-react";

// Schema for organization and user data
const orgSchema = z.object({
  name: z.string().min(1, "Organization name is required"),
  consent_to_text: z.boolean().refine((val) => val === true, {
    message: i18n.t("pages.setup.youMustConsentTo"),
  }),
});

export default function Setup() {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const { user, isSignedIn } = useUser();
  const { organization } = useOrganization();
  const clerk = useClerk();
  const [showOrgForm, setShowOrgForm] = useState(false);
  const { apiRequest } = useApiRequest();

  // Check if user is already signed in
  useEffect(() => {
    if (isSignedIn && user) {
      setShowOrgForm(true);
    }
  }, [isSignedIn, user]);

  // Organization setup form
  const orgForm = useForm({
    resolver: zodResolver(orgSchema),
    defaultValues: {
      name: "",
      consent_to_text: false,
    },
  });

  const onCompleteSetup = async (data: z.infer<typeof orgSchema>) => {
    if (!user) {
      toast({
        title: t("pages.setup.authenticationRequired"),
        description: t("pages.setup.pleaseSignUpOr"),
        variant: "destructive",
      });
      return;
    }

    try {
      setIsLoading(true);

      let orgId;

      if (!organization) {
        // Create a new organization
        const newOrg = await clerk.createOrganization({
          name: data.name,
        });

        // Set the organization as active
        await clerk.setActive({ organization: newOrg.id });
        orgId = newOrg.id;
      } else {
        // Update existing organization name if needed
        if (organization.name !== data.name) {
          await organization.update({
            name: data.name,
          });
        }
        orgId = organization.id;
      }

      // Create organization record in database (required for foreign keys)
      await apiRequest("POST", "/api/orgs", {
        name: data.name,
      });

      // Store organization phone in Clerk metadata (separate from user's personal phone)
      await apiRequest("PATCH", `/api/orgs/${orgId}/metadata`, {
        publicMetadata: {
          phone: user.primaryPhoneNumber?.phoneNumber || "",
        },
      });

      // Save user to our database
      await apiRequest("POST", "/api/users", {
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        phone: user.primaryPhoneNumber?.phoneNumber || "",
        email: user.primaryEmailAddress?.emailAddress || "",
        role: "owner", // Set as owner since this is setup
        uid: user.id,
      });

      // Update user metadata via backend API
      await apiRequest("POST", "/api/setup-complete", {
        consentToText: data.consent_to_text,
        orgName: data.name,
        phone: user.primaryPhoneNumber?.phoneNumber || "",
      });

      toast({ title: t("pages.setup.setupComplete") });
      setLocation("/orders/new");
    } catch (err: any) {
      toast({
        title: t("pages.setup.setupFailed"),
        description: err.message || "An unexpected error occurred",
        variant: "destructive",
      });
      console.error("Setup error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Card className="w-full max-w-md mx-4">
        <CardHeader>
          <h1 className="text-2xl font-bold text-center">
            {t("pages.setup.setupYourOrganization")}
          </h1>
        </CardHeader>
        <CardContent>
          {!showOrgForm ? (
            <SignUp
              routing="virtual"
              signInUrl="/login"
              redirectUrl="/setup"
              appearance={{
                elements: {
                  formButtonPrimary:
                    "bg-primary hover:bg-primary/90 text-primary-foreground",
                  card: "shadow-none",
                  headerTitle: "hidden",
                  headerSubtitle: "hidden",
                  formFieldLabel: "text-sm font-medium",
                  formFieldInput:
                    "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
                },
              }}
            />
          ) : (
            <Form {...orgForm}>
              <form
                onSubmit={orgForm.handleSubmit(onCompleteSetup)}
                className="space-y-4"
              >
                <FormField
                  control={orgForm.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("pages.setup.organizationName")}</FormLabel>
                      <FormControl>
                        <Input placeholder={t("pages.setup.acmeInc")} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={orgForm.control}
                  name="consent_to_text"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md p-4">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>
                          {t("pages.setup.iConsentToReceive")}
                        </FormLabel>
                        <FormMessage />
                      </div>
                    </FormItem>
                  )}
                />
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? t("pages.setup.creatingOrganization") : t("pages.setup.completeSetup")}
                </Button>
              </form>
            </Form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
